import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useRef } from "react";
import * as Styled from "./SortCell.styled";
import { SORT_DIRECTIONS } from "./sort-direction";
export const defaultSortMethodFor = (index, direction) => {
    const sortDirection = direction === "descending" ? -1 : 1;
    return (rowA, rowB) => {
        if (!rowA.cells[index] || !rowB.cells[index]) {
            return 0;
        }
        const sortA = rowA.cells[index].sortValue ?? rowA.cells[index].children ?? 0;
        const sortB = rowB.cells[index].sortValue ?? rowB.cells[index].children ?? 0;
        if (sortA < sortB) {
            return -1 * sortDirection;
        }
        if (sortA > sortB) {
            return Number(sortDirection);
        }
        return 0;
    };
};
export const SortCell = forwardRef((props, ref) => {
    const { ascendingSortText = defaults.ascendingSortText, children, columnIndex = 0, createSortMethodFor, descendingSortText = defaults.descendingSortText, iconProps, onToggle, sortDirection = defaults.sortDirection, textAlign = defaults.textAlign, unsortedSortText = defaults.unsortedSortText, ...otherProps } = props;
    const contentRef = useRef(null);
    const hasChildInteractionsRef = useRef(false);
    const toggleSort = useCallback(() => {
        const nextSortDirection = sortDirection === SORT_DIRECTIONS.DESC ? SORT_DIRECTIONS.ASC : SORT_DIRECTIONS.DESC;
        const sortMethod = createSortMethodFor
            ? createSortMethodFor(columnIndex, nextSortDirection)
            : defaultSortMethodFor(columnIndex, nextSortDirection);
        if (onToggle) {
            onToggle(columnIndex, nextSortDirection, sortMethod);
        }
    }, [sortDirection, onToggle, columnIndex, createSortMethodFor]);
    const getScreenReaderSortLabel = useCallback((ascendingSortText, descendingSortText, unsortedSortText) => {
        if (sortDirection === SORT_DIRECTIONS.ASC) {
            return ascendingSortText;
        }
        if (sortDirection === SORT_DIRECTIONS.DESC) {
            return descendingSortText;
        }
        return unsortedSortText;
    }, [sortDirection]);
    useEffect(() => {
        if (!contentRef.current) {
            return;
        }
        // Store whether or not clickable children are present; will always have 1 - the sort button itself
        hasChildInteractionsRef.current = contentRef.current.querySelectorAll("a,button").length > 1;
    }, [children, contentRef]);
    /** Prevent toggling sort on click of links or buttons  */
    const handleContentClick = useCallback((event) => {
        const hasClickableChildren = hasChildInteractionsRef.current;
        const isContentWrapper = event.target === event.currentTarget;
        if (!hasClickableChildren || isContentWrapper) {
            toggleSort();
        }
    }, [toggleSort]);
    return (_jsx(Styled.SortCell, { ref: ref, "aria-sort": sortDirection, sortDirection: sortDirection, palette: "interactiveConstructive", isHeader: true, scope: "col", textAlign: textAlign, ...otherProps, children: _jsxs(Styled.Content, { ref: contentRef, palette: "interactiveConstructive", onClick: handleContentClick, sortDirection: sortDirection, children: [children, _jsx(Styled.SortButton, { onClick: toggleSort, palette: "interactiveConstructive", sortDirection: sortDirection, children: _jsx(Styled.SortIcon, { palette: "interactiveConstructive", sortDirection: sortDirection, size: 16, "aria-label": getScreenReaderSortLabel(ascendingSortText, descendingSortText, unsortedSortText), ...iconProps }) })] }) }));
});
SortCell.displayName = "SortCell";
const defaults = {
    ascendingSortText: "ascending, update sort",
    descendingSortText: "descending, update sort",
    sortDirection: "none",
    textAlign: "inherit",
    unsortedSortText: "unsorted, update sort"
};
SortCell.defaultProps = defaults;
